import React, { useMemo, useState, useEffect, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { routes } from "../routes/routes";
import DataTableGeneric from "../components/DataTableGeneric";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import SearchInput from "../components/SearchInput";
import BulkLoadModal from "../components/Modal/BulkLoadModal";
import BulkLoadResultsModal from "../components/Modal/BulkLoadResultsModal";
import useFetchQuestions from "../hooks/useFetchQuestions";
import useImportQuestionsFromExcel from "../hooks/useImportQuestionsFromExcel";
import EditQuestionModal from "../components/Modal/EditQuestionModal";
import useUpdateQuestion from "../hooks/useUpdateQuestion";
import useDeleteQuestion from "../hooks/useDeleteQuestion";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import { exportErrorsToExcel } from "../utils/exportErrorsToExcel";

// Columnas para la tabla
const questionsColumns = [
  { id: "id", label: "ID" },
  { id: "questionCode", label: "Código" },
  { id: "statement", label: "Enunciado" },
  { id: "kind", label: "Tipo" },
  { id: "aspectExternalId", label: "Aspecto (ID externo)" },
  { id: "answerTypeExternalId", label: "Tipo de respuesta (ID externo)" },
  { id: "metadata", label: "Metadata" },
  { id: "actions", label: "Acciones", align: "right", minWidth: 160 },
];
// Esquema para exportar errores en la carga de preguntas desde Excel
const QUESTIONS_ERRORS_SCHEMA = [
  { header: "Fila en el archivo de cargue", width: 8, key: "row" },
  { header: "Código de pregunta", width: 20, key: "question_code" },
  {
    header: "Mensajes de errores",
    width: 70,
    value: (item, _errs, parse) => parse(item?.message || item?.error || ""),
  },
];

const QuestionsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { data: questionsData = [], error, loading, refetch } = useFetchQuestions();
  const { importFromExcel, loading: importLoading } = useImportQuestionsFromExcel();

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const { updateQuestion, loading: updating } = useUpdateQuestion();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const { deleteQuestion, loading: deleting } = useDeleteQuestion();

  const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_preguntas_plantilla.xlsx`;

  // Manejo de errores
  useEffect(() => {
    if (error) {
      setErrorMessage(error?.message || "Error al cargar las preguntas.");
      setShowError(true);
    }
  }, [error]);

  const normalizedQuery = searchTerm.trim().toLowerCase();

  const rows = useMemo(() => {
    const filtered = (questionsData || []).filter((q) => {
      if (!normalizedQuery) return true;
      const byCode = (q?.question_code || "").toString().toLowerCase().includes(normalizedQuery);
      const byAspect = (q?.aspect_external_id || "").toString().toLowerCase().includes(normalizedQuery);
      const byAnswerType = (q?.answer_type_external_id || "").toString().toLowerCase().includes(normalizedQuery);
      const byStatement = (q?.statement || "").toString().toLowerCase().includes(normalizedQuery);
      return byCode || byAspect || byAnswerType || byStatement;
    });

    return filtered.map((item) => ({
      id: item.id,
      questionCode: item.question_code ?? "-",
      statement: item.statement?.trim() || "[Sin enunciado]",
      kind: item.kind_label || item.kind || "-",
      aspectExternalId: item.aspect_external_id || (
        <Typography variant="body2" color="text.secondary">
          [Sin aspecto]
        </Typography>
      ),
      answerTypeExternalId: item.answer_type_external_id || (
        <Typography variant="body2" color="text.secondary">
          [Sin tipo]
        </Typography>
      ),
      metadata: (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {(() => {
            try {
              return JSON.stringify(item.metadata ?? {}, null, 2);
            } catch {
              return String(item.metadata);
            }
          })()}
        </Typography>
      ),
      actions: (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setSelectedQuestion({
                id: item.id,
                question_code: item.question_code ?? "",
                statement: item.statement ?? "",
                kind: item.kind ?? "single_choice",
                aspect_external_id: item.aspect_external_id ?? "",
                answer_type_external_id: item.answer_type_external_id ?? "",
                metadata: item.metadata ?? null,
              });
              setShowEditModal(true);
            }}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() =>
              openDeleteModal({
                id: item.id,
                code: item.question_code ?? null,
                statement: item.statement?.trim() ?? null,
              })
            }
          >
            Eliminar
          </Button>
        </Stack>
      ),
    }));
  }, [questionsData, normalizedQuery]);

  const handleOpenImport = () => setShowImportModal(true);

  const [showImportModal, setShowImportModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [excelResult, setExcelResult] = useState(null);

  // Manejo de errores desde el modal de edición
  const handleChildHookError = useCallback((msg) => {
    setErrorMessage(msg || "Ha ocurrido un error.");
    setShowError(true);
  }, []);

  const handleImportFromExcel = async (file) => {
    const result = await importFromExcel(file);

    if (result?.errorMessage) {
      setErrorMessage(result.errorMessage.message || "Hubo un error al procesar el archivo.");
      setShowError(true);
      return false;
    }

    if (result?.data?.error_details) {
      const encabezadoErr = (result.data.error_details || []).find((e) => {
        const msg = (e?.message || e?.error || "").toString().toLowerCase();
        return msg.includes("formato") || msg.includes("estructura") || msg.includes("falta");
      });
      if (encabezadoErr) {
        setErrorMessage("El archivo no contiene la estructura correcta.");
        setShowError(true);
        return false;
      }
    }

    if (result?.data) {
      setExcelResult(result.data);
      setShowResultsModal(true);
      return true;
    }

    setErrorMessage("No se pudo procesar el archivo.");
    setShowError(true);
    return false;
  };

  const handleCloseResults = async () => {
    setShowResultsModal(false);
    if ((excelResult?.created ?? 0) > 0 || (excelResult?.updated ?? 0) > 0) {
      await Promise.allSettled([refetch()]);
    }
  };

  const handleSubmitEdit = async (id, body) => {
    try {
      await updateQuestion(id, body);
      setSuccessMessage("La pregunta fue actualizada correctamente.");
      setShowSuccess(true);
      await refetch();
      return true;
    } catch (e) {
      setErrorMessage(e?.message || "No se pudo actualizar la pregunta.");
      setShowError(true);
      return false;
    }
  };

  const openDeleteModal = (row) => {
    setQuestionToDelete(row);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setQuestionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!questionToDelete?.id) return;
    try {
      await deleteQuestion(questionToDelete.id);
      setSuccessMessage("La pregunta fue eliminada correctamente.");
      setShowSuccess(true);
      closeDeleteModal();
      await refetch();
    } catch (e) {
      const status = e?.backend?.status ?? e?.status;
      let msg = e?.backend?.message || e?.message || "No se pudo eliminar la pregunta.";
      if (status === 409) {
        msg = "La pregunta está en uso por secciones y no puede eliminarse.";
      }
      setErrorMessage(msg);
      setShowError(true);
    }
  };

  return (
    <div className="min-w-full p-10 overflow-x-auto">
      <BreadcrumbsComponent
        breadcrumbs={[
          <Link underline="none" key="1" color="inherit" href={routes.root}>
            Página Principal
          </Link>,
          <Typography key="2" color="secondary" sx={{ fontSize: "14px" }}>
            Preguntas
          </Typography>,
        ]}
      />

      {/* Encabezado */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333333" }}>
          Listado de preguntas
        </Typography>

        <Box gap={2} sx={{ display: "flex", alignItems: "center" }}>
          <Button variant="outlined" startIcon={<AddIcon />} sx={{ height: 40 }} disabled={importLoading} onClick={handleOpenImport}>
            {importLoading ? "Importando..." : "Importar preguntas desde Excel"}
          </Button>
        </Box>
      </Box>

      {/* Buscador */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <SearchInput
            id="search-questions"
            placeholder={"Buscar por código, aspecto o tipo de respuesta..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>

        {/* Tabla */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <CircularProgress />
          </div>
        ) : (
          <DataTableGeneric columns={questionsColumns} rows={rows} />
        )}
      </Box>

      {/* Snackbars */}
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Ha ocurrido un error" message={errorMessage} />
      <SuccessSnackbar open={showSuccess} onClose={() => setShowSuccess(false)} title="Completado" message={successMessage} />

      {/* Modal de resultados de carga por Excel */}
      <BulkLoadResultsModal
        open={showResultsModal}
        onClose={handleCloseResults}
        result={excelResult}
        title="Resultado de importación de preguntas"
        labels={{
          total: "Total filas",
          created: "Creadas",
          updated: "Actualizadas",
          failed: "Errores",
          skipped: "Sin cambios",
        }}
        keys={{
          total: "total",
          created: "created",
          updated: "updated",
          failed: "failed",
          errorDetails: "error_details",
          skipped: "skipped",
        }}
        onDownloadErrors={(details) =>
          exportErrorsToExcel(details, {
            filename: "errores_preguntas_lote.xlsx",
            sheetName: "Errores",
            schema: QUESTIONS_ERRORS_SCHEMA,
          })
        }
      />

      {/* Modal de carga de archivo Excel */}
      <BulkLoadModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSelectFile={handleImportFromExcel}
        title="Importar preguntas por Excel"
        description="Descargue la plantilla requerida, complétela y súbala para crear las preguntas."
        template={{ url: TEMPLATE_URL, label: "Descargar plantilla" }}
        accept=".xlsx"
        maxSizeMB={10}
        uploadButtonLabel={importLoading ? "Importando..." : "Subir y procesar"}
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={[
          "Los tipos válidos de preguntas son: 'single_choice', 'open_text' y 'context'.",
          "Para preguntas open_text, el tipo de respuesta se asigna automáticamente a 'texto'.",
        ]}
        closeOnSuccess={true}
      />

      {/* Modal de edición de pregunta */}
      <EditQuestionModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedQuestion(null);
        }}
        initialData={selectedQuestion}
        onSubmit={handleSubmitEdit}
        loading={updating}
        onError={handleChildHookError}
      />

      {/* Modal de confirmación para eliminar */}
      <ConfirmationModal
        open={showDeleteModal}
        handleClose={closeDeleteModal}
        handleConfirm={handleConfirmDelete}
        title="¿Eliminar la pregunta?"
        description={
          questionToDelete
            ? `Se eliminará la pregunta con código “${questionToDelete.code ?? `#${questionToDelete.id}`}”. Esta acción no se puede deshacer.`
            : "Esta acción no se puede deshacer."
        }
        confirmText={deleting ? "Eliminando..." : "Eliminar"}
        cancelText="Cancelar"
        confirmColor="error"
      />
    </div>
  );
};

export default QuestionsPage;
